import React, { memo } from 'react';

import { AnalyticsPage } from 'lib/analytics/constants';
import Breadcrumbs from 'shared/components/Breadcrumbs';
import { ProductReview } from 'shared/components/ProductReview/ProductReview';
import type {
  BreadCrumbNav,
  CategoryModel,
  DynamicPageDataModel,
  LocationModel
} from 'types/shared';

import HeroAutoComplete from './components/HeroAutoComplete';
import HeroSubtitle from './components/HeroSubtitle';
import { HeroSubtitleStyles } from './components/HeroSubtitle/HeroSubtitle';
import HeroTitle from './components/HeroTitle';
import * as Styled from './styled/Hero';

type HeroProps = {
  autocompleteTitle: string;
  breadcrumbNav?: BreadCrumbNav;
  category?: Pick<
    CategoryModel,
    'slug' | 'singular' | 'plural' | 'id' | 'noun' | 'verb' | 'name'
  >;
  downgradeHeadingLevels?: boolean;
  dynamicPageData?: Pick<DynamicPageDataModel, 'job_form_lead'>;
  heroImage?: string;
  kind?: 'light' | 'primary';
  location?: Pick<
    LocationModel,
    'autocomplete' | 'postcode' | 'state' | 'city'
  >;
  mainTitle?: string;
  removeDropdown?: boolean;
  subTitle?: string;
  showProductReview?: boolean;
  subTitleStyleOf?: HeroSubtitleStyles;
  subtitleAsElement?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'span' | 'p' | 'label';
  jobFormInitiatedPageName?: AnalyticsPage;
  inExperimentalCategory?: boolean;
};

const Hero = ({
  autocompleteTitle,
  breadcrumbNav = [],
  category,
  location,
  downgradeHeadingLevels = false,
  subTitleStyleOf,
  dynamicPageData,
  heroImage,
  kind = 'primary',
  mainTitle,
  subTitle,
  removeDropdown = false,
  showProductReview,
  subtitleAsElement,
  jobFormInitiatedPageName,
  inExperimentalCategory = false
}: HeroProps) => {
  // new API for subtitle style
  // required for backward compatibility on places where the subtitle set as H3 via downgradeHeadingLevels props
  const subTitleStyleOfValue = downgradeHeadingLevels
    ? HeroSubtitleStyles.H3
    : subTitleStyleOf || HeroSubtitleStyles.H2;

  return (
    <Styled.HeroStyled $kind={kind} id="site_form">
      {breadcrumbNav.length > 1 && (
        <Breadcrumbs navigation={breadcrumbNav} kind="hero" />
      )}
      {heroImage && (
        <Styled.HeroImage
          alt="Hero image"
          src={heroImage}
          fill
          quality={100}
          priority
          sizes="100vw"
          style={{ objectFit: 'cover' }}
        />
      )}
      <Styled.HeroContentStyled>
        <Styled.TitlesWrapper>
          <HeroTitle
            downgradeHeadingLevels={downgradeHeadingLevels}
            heroTitle={mainTitle || `Best ${category.plural} near you`}
            kind={kind}
          />
          {subTitle && (
            <HeroSubtitle
              as={subtitleAsElement}
              styleOf={subTitleStyleOfValue}
              subTitle={subTitle}
            />
          )}
        </Styled.TitlesWrapper>
        <Styled.SearchStyled $kind={kind}>
          <Styled.LabelStyled $kind={kind} htmlFor="location-hero">
            {autocompleteTitle}
          </Styled.LabelStyled>
          <HeroAutoComplete
            kind={kind}
            category={category}
            location={location}
            removeDropdown={removeDropdown}
            placeholder="e.g. Sydney, 2000"
            inExperimentalCategory={inExperimentalCategory}
            jobFormInitiatedPageName={jobFormInitiatedPageName}
          />
          {dynamicPageData && dynamicPageData.job_form_lead && (
            <Styled.JobFormLeadStyled>
              {dynamicPageData.job_form_lead}
            </Styled.JobFormLeadStyled>
          )}
        </Styled.SearchStyled>
        {showProductReview && <ProductReview />}
      </Styled.HeroContentStyled>
    </Styled.HeroStyled>
  );
};

export default memo(Hero);
